import { I18n } from "@assets/strings";
import { useObservable } from "micro-observables";
import React, { useMemo } from "react";
import styled from "styled-components";
import { VideoSection } from "@domain/lms/article/article";
import { getAssetById } from "@domain/lms/article/utils";
import { PrimaryMedium } from "@ui/components/containers/primary";
import { CameraIcon, YoutubeIcon } from "@ui/components/icons";
import { Row } from "@ui/components/layout";
import { TooshareVideoPlayer } from "@ui/components/media/tooshareVideoPlayer";
import { VideoPlayer } from "@ui/components/media/videoPlayer";
import { useEditorController } from "@ui/pages/lms/article/articleEditorPage/articleEditorController/editorControllerContext";

interface VideoSectionEditorProps extends React.HTMLAttributes<HTMLDivElement> {
	section: VideoSection;
	collapsed?: boolean;
	onExtend?: () => void;
}
export const VideoSectionEditor: React.FC<VideoSectionEditorProps> = ({
	section,
	collapsed = false,
	onExtend,
	...htmlProps
}) => {
	const articleController = useEditorController();
	const videoController = useMemo(() => articleController.videoSectionController(section), []);
	const observableArticle = useObservable(articleController.article);

	const asset = "assetId" in section && getAssetById(observableArticle.content, section.assetId);
	return (
		<Container {...htmlProps}>
			{"url" in section && section.url != null ? (
				<>
					<InputWrapper onClick={() => collapsed && onExtend && onExtend()}>
						<YoutubeIcon />
						<input
							placeholder={I18n.t("article.editor.section.video.placeholder")}
							value={section.url}
							onChange={(e) => videoController.setUrl(e.currentTarget.value)}
						/>
					</InputWrapper>
					{!collapsed && <MargedVideoPlayer source={section.url} />}
				</>
			) : asset ? (
				<>
					{collapsed ? (
						<Row gap={6} align="center">
							<CameraIcon />
							<PrimaryMedium>{asset.displayName}</PrimaryMedium>
						</Row>
					) : (
						<TooshareVideoPlayer asset={asset} />
					)}
				</>
			) : null}
		</Container>
	);
};

const Container = styled.div`
	display: flex;
	flex-direction: column;

	.quality-control {
		display: none;
	}
`;

const InputWrapper = styled.div`
	display: flex;
	align-items: center;
	svg {
		width: 20px;
		height: 20px;
		margin-right: 10px;
	}

	input {
		font-size: 0.75rem;
		flex: 1;
		border: none;
		outline: none;
		text-overflow: ellipsis;
		padding-right: 10px;
	}
`;

const MargedVideoPlayer = styled(VideoPlayer)`
	margin-top: 10px;
`;
